"use client"

import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Edit2, GitBranch, Users, Calendar, BarChart4, Play, Pause, Terminal, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FormModal from '@/components/FormModal';

interface WorkflowDetailsProps {
  publicId: string;
}

export default function Workflow({ publicId }: WorkflowDetailsProps) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [workflow, setWorkflow] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchWorkflow = async () => {
      setIsLoading(true);
      try {
        // Replace with actual API call when implemented
        // const response = await WorkflowService.getWorkflow(publicId);
        // Mocking data for now
        const mockWorkflow = {
          name: "Customer Onboarding",
          description: "Process for onboarding new customers",
          status: "Active",
          createdAt: "2023-10-15",
          updatedAt: "2023-11-20",
          totalExecutions: 158,
          successRate: 92,
          trigger: "New Customer Created",
          owner: {
            name: "Jane Smith",
            id: "user-456"
          },
          steps: [
            {
              id: "step-1",
              name: "Welcome Email",
              type: "Email",
              status: "Active",
              executionTime: "1 minute"
            },
            {
              id: "step-2",
              name: "Schedule Kickoff Call",
              type: "Task",
              status: "Active",
              executionTime: "5 minutes"
            },
            {
              id: "step-3",
              name: "Create Account",
              type: "Function",
              status: "Active",
              executionTime: "2 minutes"
            },
            {
              id: "step-4",
              name: "Send Welcome Package",
              type: "Task",
              status: "Active",
              executionTime: "3 days"
            }
          ],
          recentExecutions: [
            {
              id: "exec-1",
              date: "2023-11-20",
              status: "Completed",
              duration: "3h 15m",
              subject: "Acme Corp"
            },
            {
              id: "exec-2",
              date: "2023-11-18",
              status: "Failed",
              duration: "12m",
              subject: "XYZ Inc.",
              error: "API Rate Limit Exceeded"
            },
            {
              id: "exec-3",
              date: "2023-11-15",
              status: "Completed",
              duration: "3h 22m",
              subject: "ABC Ltd"
            }
          ]
        };
        
        setTimeout(() => {
          setWorkflow(mockWorkflow);
          setIsLoading(false);
        }, 500);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load workflow details",
          variant: "destructive",
        });
        setIsLoading(false);
      }
    };
    
    if (publicId) {
      fetchWorkflow();
    }
  }, [publicId, toast]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading workflow details...</p>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <p className="text-muted-foreground">Workflow not found</p>
        <Button onClick={() => window.history.back()} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  // Get step type icon
  const getStepIcon = (type: string) => {
    switch (type) {
      case 'Email':
        return <Mail className="w-4 h-4" />;
      case 'Task':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'Function':
        return <Terminal className="w-4 h-4" />;
      default:
        return <GitBranch className="w-4 h-4" />;
    }
  };

  // Get execution status badge
  const getExecutionStatusBadge = (status: string) => {
    switch (status) {
      case 'Completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Completed</Badge>;
      case 'Failed':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Failed</Badge>;
      case 'Running':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Running</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">
              {workflow.name}
            </h1>
            <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">
              {workflow.status}
            </Badge>
          </div>
          
          <div className="flex gap-2">
            {workflow.status === 'Active' ? (
              <Button 
                variant="outline"
                className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
                onClick={() => {
                  toast({
                    title: "Success",
                    description: "Workflow paused",
                  });
                }}
              >
                <Pause className="w-4 h-4" />
                Pause Workflow
              </Button>
            ) : (
              <Button 
                variant="outline"
                className="gap-2 border-green-200 text-green-700 hover:bg-green-50"
                onClick={() => {
                  toast({
                    title: "Success",
                    description: "Workflow activated",
                  });
                }}
              >
                <Play className="w-4 h-4" />
                Activate Workflow
              </Button>
            )}
            <Button 
              onClick={() => setModalOpen(true)} 
              variant="outline"
              className="gap-2 border-primary/20 text-primary hover:bg-primary/10"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </Button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Workflow Overview */}
          <Card className="md:col-span-1 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-primary" />
                Workflow Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Owner</p>
                    <p className="text-sm text-muted-foreground">{workflow.owner.name}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Created</p>
                    <p className="text-sm text-muted-foreground">{workflow.createdAt}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Play className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Trigger</p>
                    <p className="text-sm text-muted-foreground">{workflow.trigger}</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-primary/5 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Executions</p>
                  <p className="text-2xl font-bold">{workflow.totalExecutions}</p>
                </div>
                <div className="bg-primary/5 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Success Rate</p>
                  <p className="text-2xl font-bold">{workflow.successRate}%</p>
                </div>
              </div>
              
              <Separator />
              
              {/* Quick Actions */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Quick Actions</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Play className="w-4 h-4 mr-2" />
                    Test Run
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <BarChart4 className="w-4 h-4 mr-2" />
                    Analytics
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Right Column - Workflow Details */}
          <div className="md:col-span-2 space-y-6">
            <Tabs defaultValue="steps" className="w-full">
              <TabsList className="grid grid-cols-3 w-full md:w-[400px]">
                <TabsTrigger value="steps">Steps</TabsTrigger>
                <TabsTrigger value="executions">Recent Executions</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
              </TabsList>
              
              <TabsContent value="steps" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {workflow.steps.map((step: any, index: number) => (
                        <div key={step.id} className="relative">
                          {index < workflow.steps.length - 1 && (
                            <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gray-200 -z-10"></div>
                          )}
                          <div className="flex items-start gap-4 p-4 border rounded-lg">
                            <div className="bg-primary/10 p-2 rounded-full">
                              {getStepIcon(step.type)}
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <div>
                                  <p className="font-medium">{step.name}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {step.type} • {step.executionTime}
                                  </p>
                                </div>
                                <Badge variant={step.status === 'Active' ? 'default' : 'outline'} className="h-fit">
                                  {step.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="executions" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {workflow.recentExecutions.map((execution: any) => (
                        <div key={execution.id} className="flex items-start gap-4 p-4 border rounded-lg">
                          <div className="bg-primary/10 p-2 rounded-full">
                            {execution.status === 'Completed' ? 
                              <CheckCircle2 className="w-4 h-4 text-green-600" /> : 
                              <XCircle className="w-4 h-4 text-red-600" />
                            }
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{execution.subject}</p>
                                  {getExecutionStatusBadge(execution.status)}
                                </div>
                                {execution.error && (
                                  <p className="text-xs text-red-600 mt-1">Error: {execution.error}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-sm">{execution.date}</p>
                                <p className="text-xs text-muted-foreground mt-1">Duration: {execution.duration}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="details" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Description</p>
                        <p className="font-medium">{workflow.description}</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Status</p>
                          <p className="font-medium">{workflow.status}</p>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Last Updated</p>
                          <p className="font-medium">{workflow.updatedAt}</p>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Trigger</p>
                          <p className="font-medium">{workflow.trigger}</p>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Total Steps</p>
                          <p className="font-medium">{workflow.steps.length}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      
      {/* Edit Modal - To be implemented */}
      {isModalOpen && (
        <FormModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          title="Edit Workflow"
        >
          <div className="p-4">
            <p>Workflow edit form will be implemented here.</p>
          </div>
        </FormModal>
      )}
    </div>
  );
} 